import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { PrivacyProvider } from "@/contexts/PrivacyContext";
import { FieldConfigProvider } from "@/contexts/FieldConfigContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppLockProvider } from "@/contexts/AppLockContext";
import AppLockOverlay from "@/components/AppLockOverlay";
import NewPasscodePrompt from "@/components/NewPasscodePrompt";
import { BackToTop } from "@/components/BackToTop";
import Dashboard from "@/pages/Dashboard";
import GuestDetail from "@/pages/GuestDetail";
import TourSheet from "@/pages/TourSheet";
import GuestProfile from "@/pages/GuestProfile";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PendingApproval from "@/pages/PendingApproval";
import LegalGate from "@/components/LegalGate";

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} richColors />;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#E0575B]" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Approval gate: users who aren't approved yet can't reach the app.
  if (user.status === "pending" || user.status === "denied") {
    return <PendingApproval />;
  }
  // Consent gate: approved users must accept the current Terms & Privacy Policy.
  if (user.needs_agreement) {
    return <LegalGate />;
  }
  return children;
}

// Remembers scroll position per history entry so hitting Back returns you to the exact
// spot (any page in the app). The active entry's key is read from window.history.state
// (browser-native, race-free) so saves are always attributed to the correct entry, even
// mid-navigation. Restore uses a persistent rAF loop that re-applies the saved position
// while async content loads; it fires once per real navigation (survives StrictMode remounts).
let smLastHandledKey = null;
let smStarted = false;
let smRestoreTarget = null;
let smRestoreUntil = 0;

function smKey() {
  try { return window.history.state?.key || "default"; } catch { return "default"; }
}

function smStart() {
  if (smStarted) return;
  smStarted = true;
  if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  window.addEventListener("scroll", () => {
    try { sessionStorage.setItem(`sp:${smKey()}`, String(window.scrollY)); } catch { /* ignore */ }
  }, { passive: true });
  const tick = () => {
    if (smRestoreTarget !== null) {
      if (performance.now() > smRestoreUntil) {
        smRestoreTarget = null;
      } else {
        window.scrollTo(0, smRestoreTarget);
        if (Math.abs(window.scrollY - smRestoreTarget) <= 2) smRestoreTarget = null;
      }
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const key = location.key || "default";

  useEffect(() => {
    smStart();
    if (smLastHandledKey === key) return; // once per real navigation
    smLastHandledKey = key;
    if (navType === "POP") {
      const target = Number(sessionStorage.getItem(`sp:${key}`) || 0);
      if (target > 0) { smRestoreTarget = target; smRestoreUntil = performance.now() + 4000; }
      else { smRestoreTarget = null; window.scrollTo(0, 0); }
    } else {
      smRestoreTarget = null;
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, navType]);

  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Detect the OAuth return fragment synchronously (before ProtectedRoute runs).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollManager />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/guest/new" element={<ProtectedRoute><TourSheet /></ProtectedRoute>} />
        <Route path="/guest/:id" element={<ProtectedRoute><TourSheet /></ProtectedRoute>} />
        <Route path="/profile/:name" element={<ProtectedRoute><GuestProfile /></ProtectedRoute>} />
      </Routes>
      <BackToTop />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PrivacyProvider>
          <FieldConfigProvider>
            <AppLockProvider>
              <div className="App">
                <div className="app-glow" aria-hidden="true" />
                <BrowserRouter>
                  <AppRoutes />
                </BrowserRouter>
                <ThemedToaster />
                <AppLockOverlay />
                <NewPasscodePrompt />
              </div>
            </AppLockProvider>
          </FieldConfigProvider>
        </PrivacyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
